/* eslint-disable react-refresh/only-export-components */
import { createContext, use, useMemo, type ReactNode } from "react";
import { useCfgParametrosGerais } from "@/api/space/hooks/useSpace.utilitarios-service";
import type { CfgParametrosGeraisResposta } from "@/api/space/dtos/utilitarios-service.dto";

// ------------------------------- CONSTANTS -------------------------------
const PARAMETRO_CAMINHO_IMAGENS_SERVIDOR = "caminho_imagens_servidor" as const;
const PROTOCOLOS_ARQUIVOS_PERMITIDOS = new Set(["http:", "https:"]);
const ConfiguracaoSistemaContext = createContext<ConfiguracaoSistemaContextValue | null>(null);

// --------------------------------- TYPES ---------------------------------
type ConfiguracaoSistemaContextValue = {
	readonly filesUrl: string | null;
};

type ConfiguracaoSistemaProviderProps = {
	readonly children: ReactNode;
	readonly habilitado: boolean;
};

// -------------------------------- HELPERS --------------------------------
export function obterFilesUrlConfiguracao(resposta: CfgParametrosGeraisResposta | undefined): string | null {
	const valor = resposta?.caminho_imagens_servidor?.api_exibir_imagens;
	if (typeof valor !== "string" || !valor.trim()) return null;

	try {
		const url = new URL(valor.trim());
		if (!PROTOCOLOS_ARQUIVOS_PERMITIDOS.has(url.protocol)) return null;

		return url.toString().replace(/\/+$/, "");
	} catch {
		return null;
	}
}

// ------------------------------- FUNCTIONS -------------------------------
export function useConfiguracaoSistema(): ConfiguracaoSistemaContextValue {
	const contexto = use(ConfiguracaoSistemaContext);
	if (!contexto) throw new Error("useConfiguracaoSistema deve ser usado dentro de ConfiguracaoSistemaProvider.");

	return contexto;
}

// ------------------------------- COMPONENT -------------------------------
export function ConfiguracaoSistemaProvider({ children, habilitado }: Readonly<ConfiguracaoSistemaProviderProps>) {
	const consultaParametros = useCfgParametrosGerais({
		enabled: habilitado,
		nomeParametro: PARAMETRO_CAMINHO_IMAGENS_SERVIDOR,
	});
	const filesUrl = habilitado ? obterFilesUrlConfiguracao(consultaParametros.data) : null;
	const valor = useMemo(() => ({ filesUrl }), [filesUrl]);

	return <ConfiguracaoSistemaContext value={valor}>{children}</ConfiguracaoSistemaContext>;
}
